import { Button } from "antd";
import PropTypes from "prop-types";
import { Component } from "react";
import TvDebugButton from "components/misc/TvDebugButton";
import { isDev, isTestMode } from "utils/Constant";

/**
 * ============================================================================
 * ERROR BOUNDARY COMPONENT
 * ============================================================================
 **/
// Cap on how many times we'll silently auto-recover from extension-origin
// crashes within a short window, so a misbehaving extension that throws on
// *every* render can't turn into an invisible infinite re-render loop -
// past this cap we fall back to the normal blocking crash card instead.
const MAX_AUTO_RECOVERIES = 3;
const AUTO_RECOVERY_WINDOW_MS = 10_000;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.recoveryTimestamps = [];
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Unhandled error caught by ErrorBoundary:", error);
    // Optionally forward to a tracking service if initialized
    try {
      if (
        typeof window !== "undefined" &&
        window.Sentry &&
        window.Sentry.captureException
      ) {
        window.Sentry.captureException(error, { extra: errorInfo });
      }
    } catch (e) {
      // ignore
    }

    // Persist so the TV diagnostics panel can show it even after the user
    // hits Reload/Home - the crash screen's own error text is often too
    // faint/small to read off a photo of the TV, this survives navigation.
    try {
      localStorage.setItem(
        "enlis_last_error",
        JSON.stringify({
          message: error?.toString?.() || String(error),
          errorType: typeof error,
          isErrorInstance: error instanceof Error,
          stack: error?.stack || null,
          componentStack: errorInfo?.componentStack || null,
          route:
            typeof window !== "undefined" && window.location
              ? `${window.location.pathname}${window.location.search}`
              : null,
          time: new Date().toISOString(),
        }),
      );
    } catch (e) {
      // storage unavailable - nothing we can do
    }

    // keep a copy of the error so we can surface the message in the UI
    this.setState({ error });

    // Extension-origin errors aren't a sign our own render tree/state is
    // corrupt - the exception came from code we don't own, not from a bug
    // in eN-LIS. Rather than leave the TV stuck on a blocking crash card
    // until someone manually hits Reload, auto-recover: remount children
    // almost immediately so the dashboard keeps working. Capped so a
    // pathological extension that throws on every render can't turn this
    // into a silent infinite loop - after MAX_AUTO_RECOVERIES within
    // AUTO_RECOVERY_WINDOW_MS we give up and show the normal crash card.
    if (this.isLikelyExtensionError(error)) {
      const now = Date.now();
      this.recoveryTimestamps = this.recoveryTimestamps.filter(
        (t) => now - t < AUTO_RECOVERY_WINDOW_MS,
      );
      if (this.recoveryTimestamps.length < MAX_AUTO_RECOVERIES) {
        this.recoveryTimestamps.push(now);
        setTimeout(() => {
          this.setState({ hasError: false, error: null });
        }, 50);
      }
    }
  }

  handleReload = () => window.location.reload();
  handleHome = () => (window.location.href = "/");

  // A crash whose stack points into a browser extension/userscript
  // (chrome-extension://…) isn't an eN-LIS bug - it's third-party code
  // injected into the page throwing on its own. Flag it so field staff
  // don't chase it as an app issue; the fix is disabling that extension
  // on the device.
  isLikelyExtensionError = (error) =>
    /(chrome|moz|safari|edge)-extension:\/\//i.test(error?.stack || "");

  render() {
    const { hasError, error } = this.state;
    const { children } = this.props;
    const extensionSuspect = hasError && this.isLikelyExtensionError(error);

    return (
      <>
        {(isTestMode || isDev) && (
          <TvDebugButton crashError={hasError ? error : null} />
        )}
        {hasError ? (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-white shadow-lg rounded-2xl border border-gray-100 px-6 py-4 text-center">
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-2xl text-red-500">✕</span>
                </div>
              </div>

              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                An unexpected error occurred.
              </h2>

              {error && (
                <p className="text-sm text-red-500 mb-3 break-words max-h-20 overflow-auto px-2 italic">
                  {error.toString()}
                </p>
              )}

              {extensionSuspect && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-left">
                  ⚠ This looks like it&apos;s caused by a browser extension
                  installed on this device, not eN-LIS itself. Try disabling
                  extensions/add-ons on this TV&apos;s browser.
                </p>
              )}

              <p className="text-xs text-gray-500 mb-5">
                You can try reloading the app, or go back to the homepage.
              </p>

              <div className="flex items-center justify-center gap-2">
                <Button
                  type="primary"
                  className="!bg-green-600 hover:!bg-green-700 min-w-[6rem] mb-0"
                  onClick={this.handleReload}
                >
                  Reload
                </Button>
                <Button className="min-w-[6rem] mb-0" onClick={this.handleHome}>
                  Home
                </Button>
              </div>
            </div>
          </div>
        ) : (
          children
        )}
      </>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
